import { httpClient } from '@bs/core'
import type {
  AssignedStore,
  User,
  UserDetail,
  UserListParams,
  UserListResponse,
} from '../../types/user'

interface ApiUserItem {
  _id: string
  user_id: string
  user_email: string
  full_name: string
  first_name: string
  last_name: string
  photo_url: string | null
  // List endpoint returns the same AssignedStore shape as the detail endpoint —
  // a previous version of this type used `string[]` which caused a render
  // crash in UserTable (Objects are not valid as a React child).
  assigned_stores: AssignedStore[]
  is_ga: boolean
  is_owner: boolean
  joined_at: string | null
}

// httpClient's defaultResponseHandler unwraps res.data.data → the resolved value
// is the inner data object directly, not the full { data, message, status } envelope.
interface ApiUserListResponse {
  items: ApiUserItem[]
  page: number
  limit: number
  total_items: number
  total_pages: number
  full_total: number
}

const userListUrl = (orgId: string) => `/organizations/${orgId}/users`
const userDetailUrl = (orgId: string, userId: string) =>
  `/organizations/${orgId}/users/${userId}`

const mapFromApi = (item: ApiUserItem): User => ({
  _id: item._id,
  user_id: item.user_id,
  user_email: item.user_email,
  full_name: item.full_name,
  first_name: item.first_name,
  last_name: item.last_name,
  photo_url: item.photo_url,
  assigned_stores: item.assigned_stores ?? [],
  is_ga: item.is_ga,
  is_owner: item.is_owner,
  joined_at: item.joined_at,
})

export const getUserList = async (
  orgId: string,
  params: UserListParams,
): Promise<UserListResponse> => {
  const raw = (await httpClient.json.get(userListUrl(orgId), {
    params: {
      limit: params.limit,
      page: params.page,
      ...(params.search ? { search: params.search } : {}),
      ...(params.role_id ? { role_id: params.role_id } : {}),
      ...(params.status ? { status: params.status } : {}),
    },
  })) as ApiUserListResponse

  return {
    items: (raw?.items ?? []).map(mapFromApi),
    page: raw?.page ?? 1,
    limit: raw?.limit ?? 10,
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
    fullTotal: raw?.full_total ?? 0,
  }
}

// httpClient unwraps res.data.data → resolved value is the inner data object directly
export const getUser = async (
  orgId: string,
  userId: string,
): Promise<UserDetail> => {
  return (await httpClient.json.get(userDetailUrl(orgId, userId))) as UserDetail
}

// ── Invite user ───────────────────────────────────────────────────────────────
// POST /organizations/users/invite/
// Confirmed shape from legacy Settings/Users/List/hooks.js

// `location_roles` requires all three fields per the backend
// `LocationRoleSerializer` (see `crm_be_v2_api/serializers/organization.py`).
// Sending only `{ warehouse_id }` returns a 400 — use `buildLocationRoles`
// in `features/User/utils/inviteLocationRoles.ts` to construct entries.
export interface InviteUserStore {
  store_id: string
  role_id: string
  permissions: string[]
  location_roles: Array<{
    enabled: boolean
    location_id: string
    warehouse_id: string
  }>
  custom_roles: Record<string, unknown>
}

export interface InviteUserPayload {
  organization_id: string
  target_full_name: string
  target_store_id: string
  invite_users: Array<{
    user_email: string
    is_ga: boolean
    full_name: string
    stores: InviteUserStore[]
    channels: string[]
  }>
}

export const inviteUser = async (payload: InviteUserPayload): Promise<void> => {
  await httpClient.json.post('/organizations/users/invite/', payload)
}

// ── Update user ───────────────────────────────────────────────────────────────
// PUT /organizations/{orgId}/users/{userId}
// Confirmed shape from legacy Settings/Users/Detail/index.tsx + hooks/useUserDetail.js
//
// `location_roles` shares the invite endpoint's `LocationRoleSerializer` shape —
// `{ enabled, location_id, warehouse_id }` for every linked warehouse. Empty
// array means "all locations" (no per-location restriction). The previous
// narrower `Array<{ warehouse_id: string }>` typing matched the legacy main-app
// detail form but produced the same 400s the invite path hit before widening.

export interface UpdateUserStore {
  store_id: string
  role_id: string
  permissions: string[]
  location_roles: Array<{
    enabled: boolean
    location_id: string
    warehouse_id: string
  }>
  custom_roles: Record<string, unknown>
}

export interface UpdateUserPayload {
  full_name: string
  is_ga: boolean
  assigned_stores: UpdateUserStore[]
  channels: string[]
}

export const updateUser = async (
  orgId: string,
  userId: string,
  payload: UpdateUserPayload,
): Promise<void> => {
  await httpClient.json.put(`/organizations/${orgId}/users/${userId}`, payload)
}

// ── Delete user ───────────────────────────────────────────────────────────────
// DELETE /organizations/{orgId}/users/{userId}
// Confirmed against legacy `src/pages/Settings/hooks/useUserDetail.js` (the
// `deleteUser` helper there hits the same URL). No payload — the URL identifies
// the user being removed.
export const deleteUser = async (
  orgId: string,
  userId: string,
): Promise<void> => {
  await httpClient.json.delete(`/organizations/${orgId}/users/${userId}`)
}

// ── Resend invite ─────────────────────────────────────────────────────────────
// POST /organizations/{orgId}/users/{userId}/resend-invite/
//
// Only valid for pending users (the backend hard-gates on
// OrganizationUsers.joined_at === null and returns 400 otherwise). No payload —
// the backend rebuilds the invite payload from the existing OrganizationUsers
// doc (assigned_stores, is_ga, full_name) and re-sends the email with a fresh
// 24h JWT. The frontend mirrors the same gate by only rendering the Resend
// button when `deriveStatus(user) === 'invited'`, so this call should never
// reach the 400 branch in normal use.
export const resendInvite = async (
  orgId: string,
  userId: string,
): Promise<void> => {
  // Pass an explicit empty body — `httpClient.json.post(url)` (no body) is not
  // a shape we've used elsewhere in this file; matching `inviteUser`'s call
  // signature avoids surprises with the axios wrapper.
  await httpClient.json.post(
    `/organizations/${orgId}/users/${userId}/resend-invite/`,
    {},
  )
}
